import type { ReactNode } from "react";
import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Inbox,
  LoaderCircle,
  Search,
} from "lucide-react";
import clsx from "clsx";
import { SearchableSelect } from "./SearchableSelect";

export function Card({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <section className={clsx("card", className)}>{children}</section>;
}

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="page-header">
      <div>
        <h1>{title}</h1>
        {description && <p>{description}</p>}
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </header>
  );
}

export function Badge({
  children,
  tone = "neutral",
}: {
  children: ReactNode;
  tone?: "neutral" | "navy" | "red" | "green" | "gold" | "blue";
}) {
  return <span className={`badge badge-${tone}`}>{children}</span>;
}

export function Loading({
  label = "جارٍ تحميل البيانات...",
}: {
  label?: string;
}) {
  return (
    <div className="state-box">
      <LoaderCircle className="spin" size={24} />
      <span>{label}</span>
    </div>
  );
}

export function Empty({ label = "لا توجد بيانات مطابقة" }: { label?: string }) {
  return (
    <div className="state-box">
      <Inbox size={28} />
      <span>{label}</span>
    </div>
  );
}

export function ErrorState({ message }: { message: string }) {
  return (
    <div className="state-box state-error">
      <AlertCircle size={24} />
      <span>{message}</span>
    </div>
  );
}

export function SearchBox({
  value,
  onChange,
  placeholder = "بحث...",
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  return (
    <label className="search-box">
      <Search size={18} />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </label>
  );
}

export function Metric({
  label,
  value,
  hint,
  icon,
  tone = "navy",
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  icon?: ReactNode;
  tone?: string;
}) {
  return (
    <Card className={`metric metric-${tone}`}>
      <div className="metric-top">
        <span>{label}</span>
        {icon && <i>{icon}</i>}
      </div>
      <strong>{value}</strong>
      {hint && <small>{hint}</small>}
    </Card>
  );
}

export function Progress({
  value,
  max = 100,
  tone = "red",
}: {
  value: number;
  max?: number;
  tone?: string;
}) {
  const width = Math.min(100, Math.max(0, max ? (value / max) * 100 : 0));
  return (
    <div className="progress">
      <span className={`progress-${tone}`} style={{ width: `${width}%` }} />
    </div>
  );
}

export function Pagination({
  page,
  pages,
  total,
  perPage = 25,
  onChange,
  onPerPage,
}: {
  page: number;
  pages: number;
  total?: number;
  perPage?: number;
  onChange: (page: number) => void;
  onPerPage?: (value: number) => void;
}) {
  if (pages <= 1 && !onPerPage) return null;
  const visible = Array.from(
    { length: Math.min(5, pages) },
    (_, i) => Math.max(1, Math.min(pages - 4, page - 2)) + i,
  ).filter((value) => value <= pages);
  return (
    <footer className="pagination">
      <div>
        {typeof total === "number" && (
          <span>{total.toLocaleString("ar-SA-u-nu-latn")} سجل</span>
        )}
        {onPerPage && (
          <div className="per-page-select">
            <span>عرض</span>
            <SearchableSelect
              compact
              ariaLabel="عدد السجلات في الصفحة"
              searchPlaceholder="ابحث عن عدد..."
              value={perPage}
              onChange={(value) => onPerPage(Number(value))}
              options={[10, 25, 50, 100].map((item) => ({
                value: String(item),
                label: String(item),
              }))}
            />
          </div>
        )}
      </div>
      <nav>
        <button title="الأولى" disabled={page <= 1} onClick={() => onChange(1)}>
          <ChevronsRight />
        </button>
        <button
          title="السابق"
          disabled={page <= 1}
          onClick={() => onChange(page - 1)}
        >
          <ChevronRight />
        </button>
        {visible.map((value) => (
          <button
            key={value}
            className={value === page ? "active" : ""}
            onClick={() => onChange(value)}
          >
            {value}
          </button>
        ))}
        <button
          title="التالي"
          disabled={page >= pages}
          onClick={() => onChange(page + 1)}
        >
          <ChevronLeft />
        </button>
        <button
          title="الأخيرة"
          disabled={page >= pages}
          onClick={() => onChange(pages)}
        >
          <ChevronsLeft />
        </button>
      </nav>
    </footer>
  );
}
